import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Rating,
  Card,
  CardContent,
  Button,
  Avatar,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getApprovedReviews } from '../../../apis/Admin';
import StarIcon from '@mui/icons-material/Star';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollContainerRef = useRef(null);
  const animationRef = useRef(null);
  const navigate = useNavigate();

  const MAX_REVIEW_LENGTH = 150; // Characters before showing "Read full review"

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const reviewsData = await getApprovedReviews();
        const reviewsList = Array.isArray(reviewsData)
          ? reviewsData
          : Array.isArray(reviewsData?.data)
            ? reviewsData.data
            : Array.isArray(reviewsData?.reviews)
              ? reviewsData.reviews
              : [];

        // Filter only approved reviews
        const approvedReviews = reviewsList.filter(
          (review) => review.approved || review.is_approved,
        );

        // Only duplicate if we have reviews to create seamless infinite scroll
        if (approvedReviews.length > 0) {
          setReviews([...approvedReviews, ...approvedReviews]);
        } else {
          setReviews([]);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching approved reviews:', error);
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  useEffect(() => {
    if (reviews.length === 0 || loading) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    let scrollPosition = 0;
    const scrollSpeed = 1; // pixels per frame
    let isPaused = false;
    let isUserScrolling = false;
    let scrollTimeout = null;
    let handleMouseEnter;
    let handleMouseLeave;
    let handleScroll;
    let handleWheel;

    // Wait for layout to be ready
    const timeoutId = setTimeout(() => {
      const animate = () => {
        if (!isPaused && !isUserScrolling && container) {
          scrollPosition += scrollSpeed;

          // Reset position when scrolled past half (since we duplicated the reviews)
          const halfWidth = container.scrollWidth / 2;
          if (scrollPosition >= halfWidth) {
            scrollPosition = 0;
          }

          container.scrollLeft = scrollPosition;
        }
        animationRef.current = requestAnimationFrame(animate);
      };

      // Pause on hover
      handleMouseEnter = () => {
        isPaused = true;
      };

      handleMouseLeave = () => {
        isPaused = false;
      };

      // Detect user scrolling
      handleScroll = () => {
        isUserScrolling = true;
        scrollPosition = container.scrollLeft;

        // Clear existing timeout
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }

        // Resume auto-scroll after user stops scrolling for 2 seconds
        scrollTimeout = setTimeout(() => {
          isUserScrolling = false;
        }, 2000);
      };

      // Detect wheel/touch scrolling
      handleWheel = () => {
        isUserScrolling = true;
        scrollPosition = container.scrollLeft;

        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }

        scrollTimeout = setTimeout(() => {
          isUserScrolling = false;
        }, 2000);
      };

      container.addEventListener('mouseenter', handleMouseEnter);
      container.addEventListener('mouseleave', handleMouseLeave);
      container.addEventListener('scroll', handleScroll);
      container.addEventListener('wheel', handleWheel, { passive: true });
      container.addEventListener('touchstart', handleWheel, { passive: true });
      container.addEventListener('touchmove', handleWheel, { passive: true });

      // Start animation
      animate();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      if (container) {
        if (handleMouseEnter) {
          container.removeEventListener('mouseenter', handleMouseEnter);
        }
        if (handleMouseLeave) {
          container.removeEventListener('mouseleave', handleMouseLeave);
        }
        if (handleScroll) {
          container.removeEventListener('scroll', handleScroll);
        }
        if (handleWheel) {
          container.removeEventListener('wheel', handleWheel);
          container.removeEventListener('touchstart', handleWheel);
          container.removeEventListener('touchmove', handleWheel);
        }
      }
    };
  }, [reviews, loading]);

  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          py: { xs: 6, md: 8 },
          px: { xs: 2, md: 4 },
          backgroundColor: '#f8fafc',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ color: '#6b7280' }}>Loading reviews...</Typography>
      </Box>
    );
  }

  if (reviews.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        py: { xs: 8, md: 12 },
        px: { xs: 2, md: 4 },
        position: 'relative',
        background:
          'linear-gradient(180deg, #f8fafc 0%, #ffffff 50%, #f0f7ff 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #0056D2 0%, #002C6C 100%)',
        },
      }}
    >
      {/* Decorative Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '-100px',
          right: '-100px',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(0, 86, 210, 0.08) 0%, transparent 70%)',
          filter: 'blur(60px)',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-150px',
          left: '-150px',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(0, 44, 108, 0.06) 0%, transparent 70%)',
          filter: 'blur(80px)',
          zIndex: 0,
        }}
      />

      {/* Header Section */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: '1200px',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: { xs: 3, md: 5 },
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.75,
                mb: 3,
                borderRadius: '50px',
                background:
                  'linear-gradient(135deg, rgba(0, 86, 210, 0.1) 0%, rgba(0, 44, 108, 0.1) 100%)',
                border: '1px solid rgba(0, 86, 210, 0.2)',
              }}
            >
              <StarIcon sx={{ color: '#0056D2', fontSize: '1.2rem' }} />
              <Typography
                sx={{
                  color: '#0056D2',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                }}
              >
                Customer Reviews
              </Typography>
            </Box>

            <Typography
              variant="h2"
              sx={{
                fontWeight: 650,
                fontSize: { xs: '1rem', sm: '2rem', md: '2rem' },
                background: 'linear-gradient(135deg, #0056D2 0%, #002C6C 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
                textAlign: 'center',
                letterSpacing: '-0.03em',
                lineHeight: 1.1,
                maxWidth: '900px',
                mx: 'auto',
              }}
            >
              Featured Reviews
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: '#64748b',
                fontSize: { xs: '1rem', md: '1.125rem' },
                textAlign: 'center',
                mb: 1,
                fontWeight: 400,
                maxWidth: '600px',
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Join the community of satisfied users achieving data excellence
            </Typography>

            {/* Decorative Line */}
            <Box
              sx={{
                width: '80px',
                height: '4px',
                borderRadius: '2px',
                background: 'linear-gradient(90deg, #0056D2 0%, #002C6C 100%)',
                mt: 2,
              }}
            />
          </Box>
        </motion.div>
      </Box>

      {/* Scrolling Reviews Container */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '1400px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          ref={scrollContainerRef}
          sx={{
            width: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            position: 'relative',
            maskImage:
              'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
            WebkitMaskImage:
              'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
            scrollBehavior: 'smooth',
            // Hide scrollbar but keep functionality
            '&::-webkit-scrollbar': {
              display: 'none',
              width: 0,
              height: 0,
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            // Enable scrolling on touch devices
            WebkitOverflowScrolling: 'touch',
            // Prevent vertical scrolling
            overscrollBehaviorX: 'contain',
            overscrollBehaviorY: 'none',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 2.5, md: 4 },
              width: 'max-content',
              py: 4,
            }}
          >
            {reviews.map((review, index) => {
              const rating = review.rating || 0;
              const reviewText = review.review_text || review.review || '';
              const reviewerName = review.name || 'Anonymous';
              const jobTitle = review.organization || '';
              const reviewId = review.id || review.review_id || index;
              const isLongReview = reviewText.length > MAX_REVIEW_LENGTH;
              const displayText =
                reviewText.substring(0, MAX_REVIEW_LENGTH) +
                (isLongReview ? '...' : '');

              return (
                <motion.div
                  key={`${review.id || review.review_id || index}-${Math.floor(index / (reviews.length / 2))}`}
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  whileHover={{ scale: 1.03, y: -8, zIndex: 10 }}
                >
                  <Card
                    onClick={() => navigate(`/reviews?id=${reviewId}`)}
                    sx={{
                      minWidth: { xs: 320, sm: 380, md: 400 },
                      maxWidth: { xs: 320, sm: 380, md: 400 },
                      minHeight: '350px',
                      maxHeight: '350px',
                      borderRadius: '6px',
                      position: 'relative',
                      background: '#ffffff',
                      border: '2px solid #e5e7eb',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background:
                          'linear-gradient(90deg, #0056D2 0%, #002C6C 100%)',
                        transform: 'scaleX(0)',
                        transformOrigin: 'left',
                        transition: 'transform 0.4s ease',
                        zIndex: 1,
                      },
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(0, 86, 210, 0.2)',
                        transform: 'translateY(-8px) scale(1.02)',
                        // border: '2px solid #0056D2',
                        '&::before': {
                          transform: 'scaleX(1)',
                        },
                      },
                      '&:active': {
                        transform: 'translateY(-4px) scale(1.01)',
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        flex: 1,
                        overflow: 'hidden',
                      }}
                    >
                      {/* Reviewer Info with Avatar */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          mb: 2.5,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 48,
                            height: 48,
                            bgcolor: '#5965A6',
                            color: '#ffffff',
                            fontWeight: 600,
                            fontSize: '1.1rem',
                            mr: 1.5,
                          }}
                        >
                          {reviewerName.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              color: '#1e293b',
                              fontSize: '1rem',
                              fontWeight: 600,
                              mb: 0.25,
                              lineHeight: 1.2,
                            }}
                          >
                            {reviewerName}
                          </Typography>
                          {jobTitle && (
                            <Typography
                              sx={{
                                color: '#6b7280',
                                fontSize: '0.875rem',
                                fontWeight: 400,
                                lineHeight: 1.2,
                              }}
                            >
                              {jobTitle}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Rating */}
                      <Box sx={{ mb: 2 }}>
                        <Rating
                          value={rating}
                          readOnly
                          precision={0.5}
                          icon={
                            <StarIcon
                              sx={{
                                color: '#FFD700',
                                fontSize: '1.25rem',
                              }}
                            />
                          }
                          emptyIcon={
                            <StarIcon
                              sx={{
                                color: '#e0e0e0',
                                fontSize: '1.25rem',
                              }}
                            />
                          }
                        />
                      </Box>

                      {/* Review Text */}
                      <Typography
                        sx={{
                          color: '#374151',
                          fontSize: '0.95rem',
                          lineHeight: 1.6,
                          mb: 2,
                          flexGrow: 1,
                          fontWeight: 400,
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {displayText}
                      </Typography>

                      {/* Read Full Review Button */}
                      {isLongReview && (
                        <Box
                          sx={{
                            mt: 'auto',
                            display: 'flex',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/reviews?id=${reviewId}`);
                            }}
                            endIcon={
                              <ArrowForwardIcon sx={{ fontSize: '0.875rem' }} />
                            }
                            sx={{
                              color: '#1e293b',
                              textTransform: 'none',
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              p: 0,
                              minWidth: 'auto',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                backgroundColor: 'transparent',
                                textDecoration: 'underline',
                                color: 'rgb(255, 145, 11)',
                                transform: 'translateX(4px)',
                              },
                              '& .MuiButton-endIcon': {
                                marginLeft: '4px',
                                transition: 'transform 0.3s ease',
                              },
                              '&:hover .MuiButton-endIcon': {
                                transform: 'translateX(4px)',
                              },
                            }}
                          >
                            Read full review
                          </Button>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Read More Reviews Button */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: '1200px',
          mt: 4,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate('/reviews')}
            sx={{
              borderColor: '#1e293b',
              color: '#1e293b',
              borderRadius: '8px',
              px: 4,
              py: 1.5,
              fontWeight: 600,
              fontSize: '0.95rem',
              textTransform: 'none',
              borderWidth: '1.5px',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#1e293b',
                backgroundColor: '#1e293b',
                color: '#ffffff',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(30, 41, 59, 0.2)',
              },
            }}
          >
            Read more reviews
          </Button>
        </motion.div>
      </Box>
    </Box>
  );
};

export default Reviews;
